import { useEffect, useState, useRef } from 'react';
import { useDebounce, useToggle } from 'ahooks';
import { AnimatePresence, motion } from 'framer-motion';
import { useStyles, Button, pxToRem, Flex } from '@archipro-design/aria';
import type { ButtonProps } from '@archipro-design/aria';
import {
    CheckboxBlankLine,
    CheckboxFill,
    Filter,
    SearchLine,
} from '@archipro-design/icons';
import { InPageSearchBar } from '@modules/search/components/in-page-search-bar/InPageSearchBar';
import FilterDrawer from '@modules/search/components/filter-drawer/FilterDrawer';
import type { SearchIndex } from '@modules/search/type';
import type { FilterBarCheckboxFilter } from '~/modules/search/type/filter-state';
import { useMatchesHandleData } from '~/modules/root/hook';
import { useProfessionalTabSearch } from '~/modules/professional/hook/use-professional-tab-search';
import { useCurrentTabPath } from '~/modules/professional/hook/use-current-tab-path';
import { SHOP_FILTER } from '~/modules/product/config/product-filter-config';
import * as S from './SearchFilter.style';
import { ProfessionalFilterSheet } from './ProfessionalFilterSheet';
import { ProfessionalTabListingBar } from '~/ui-rocco/pages/professional-detail/sections/ProfessionalTabListingBar';
import type { SearchFilterProp } from './SearchFilter';

/**
 * Mobile filter bar for the professional grid tabs. The revamp presentation is
 * opt-in via `searchBarVariant="revamp"` (the detail-page sections pass it), so
 * the legacy tabs keep the bar they have always had while
 * `revamp_2026` is off.
 */
export const SearchFilterMobile: React.FC<SearchFilterProp> = (props) =>
    props.searchBarVariant === 'revamp' ? (
        <RevampFilterBar {...props} />
    ) : (
        <LegacyFilterBar {...props} />
    );

/**
 * Compact revamp bar: an icon-only "Refine" circle (opens
 * {@link ProfessionalFilterSheet}, with an applied-count badge) beside an
 * always-expanded pill search. Category, sort and every facet live inside the
 * Refine sheet, so the bar stays to a single clean row. `searchBarTheme="dark"`
 * inverts it for the dark Projects tab.
 */
/** How long the field waits after the last keystroke before it searches. */
const SEARCH_DEBOUNCE_MS = 600;

const RevampFilterBar: React.FC<SearchFilterProp> = ({
    searchFilters,
    searchPlaceholder = 'Search',
    mobileSearchPlaceholder,
}) => {
    const tabSearch = useProfessionalTabSearch();
    const [filterDrawerOpen, { set: setFilterDrawerState }] = useToggle(false);

    // The field filters as the reader types, a breath after the last key:
    // the term is kept here and committed to the URL once it settles.
    const [keyword, setKeyword] = useState(tabSearch.defaultKeyword);
    const settledKeyword = useDebounce(keyword, { wait: SEARCH_DEBOUNCE_MS });
    const { onSearchChange, defaultKeyword } = tabSearch;
    // The last term this field put in the URL, so it can tell its own echo
    // from someone else's change.
    const committed = useRef(tabSearch.defaultKeyword);
    useEffect(() => {
        const settled = settledKeyword.trim();
        if (settled === defaultKeyword) return;
        committed.current = settled;
        onSearchChange(settled);
    }, [settledKeyword, defaultKeyword, onSearchChange]);
    // Back/forward and a cleared term move the committed one; follow it — but
    // not this field's own echo. The URL catches up a loader round trip after
    // the term was sent, by which time the reader has typed more, and copying
    // it back over them deleted what they had just typed.
    useEffect(() => {
        if (defaultKeyword === committed.current) return;
        committed.current = defaultKeyword;
        setKeyword(defaultKeyword);
    }, [defaultKeyword]);

    const {
        filters,
        filterStates,
        ignoreFilterKeys,
        resetFilters,
        appliedFilters,
    } = searchFilters;

    const tabIndex = useMatchesHandleData<SearchIndex>('category', 'product');
    // Active-filter count for the Refine badge. `appliedFilters` already counts
    // facet filters and the selected category (via getAppliedFilterItems),
    // mirroring the category page — getAppliedFilterNumber skipped facet arrays.
    const appliedCount = appliedFilters?.length ?? 0;

    return (
        <>
            {/* The listing tabs' bar: the directory's filter control and the
                search field open beside it. The tabs' placeholders follow
                "Search {Thing} by {Professional}"; the professional suffix
                doesn't fit the phone's field, so it's dropped here (desktop
                keeps it). */}
            <ProfessionalTabListingBar
                search={{
                    value: keyword,
                    onChange: setKeyword,
                    placeholder:
                        mobileSearchPlaceholder ??
                        searchPlaceholder.replace(/ by .*$/, ''),
                }}
                onOpenFilters={() => setFilterDrawerState(true)}
                filtersCount={appliedCount}
            />

            <ProfessionalFilterSheet
                open={filterDrawerOpen}
                onOpenChange={(next) => setFilterDrawerState(next)}
                filterSections={filters ?? []}
                filterStates={filterStates}
                category={tabIndex}
                onApplyFilters={(data) => {
                    setFilterDrawerState(false);
                    resetFilters(data);
                }}
                ignoreFilterKeys={ignoreFilterKeys}
            />
        </>
    );
};

/**
 * The long-standing mobile bar: "Filters" and "Search" text buttons that open
 * the accordion {@link FilterDrawer} and a collapsible search drawer, plus the
 * inline "Shop" checkbox on tabs that expose it.
 */
const LegacyFilterBar: React.FC<SearchFilterProp> = ({ searchFilters }) => {
    const currentTabPath = useCurrentTabPath();
    const { css } = useStyles({ currentTabPath });
    const tabSearch = useProfessionalTabSearch();
    const category = useMatchesHandleData<SearchIndex>('category', 'project');
    const [filterDrawerOpen, { set: setFilterDrawerState }] = useToggle(false);
    const [
        searchDrawerOpen,
        { toggle: toggleSearchDrawer, set: setSearchDrawer },
    ] = useToggle(false);
    const {
        filters,
        filterStates,
        filterBarFilters,
        ignoreFilterKeys,
        resetFilters,
        updateFilter,
    } = searchFilters;

    const shopFilter: FilterBarCheckboxFilter | undefined =
        filterBarFilters?.find((f) => f.filterKey === SHOP_FILTER.filterKey);

    // Open search drawer if there is a default keyword
    useEffect(() => {
        if (tabSearch.defaultKeyword) {
            setSearchDrawer(true);
        }
    }, [tabSearch.defaultKeyword, setSearchDrawer]);

    const buttonProps = {
        size: 14,
        transparent: true,
        variables: () => ({
            buttonPadding: 10,
        }),
        className: css(S.MobileButtonFilters),
    } as ButtonProps;

    return (
        <>
            <div className={css(S.MobileFilterWarp)}>
                <Flex vAlign="center">
                    <Button
                        iconBefore={<Filter aria-label={'filter'} />}
                        onClick={() => setFilterDrawerState(true)}
                        {...buttonProps}
                    >
                        Filters
                    </Button>
                    {shopFilter && (
                        <Button
                            key={SHOP_FILTER.filterKey}
                            variant="text"
                            size={18}
                            variables={{
                                buttonPadding: pxToRem(10),
                                iconPadding: 0,
                                buttonSize18TextFontSize: pxToRem(14),
                                buttonSize18LineHeight: pxToRem(14),
                            }}
                            iconBefore={
                                shopFilter.checked ? (
                                    <CheckboxFill />
                                ) : (
                                    <CheckboxBlankLine />
                                )
                            }
                            disabled={shopFilter.disabled}
                            onClick={() => {
                                updateFilter(
                                    SHOP_FILTER.filterKey,
                                    shopFilter.newState,
                                );
                            }}
                            className={css(S.MobileButtonFilters)}
                        >
                            {shopFilter.label}
                        </Button>
                    )}
                </Flex>
                <Button
                    iconAfter={<SearchLine aria-label={'search'} />}
                    onClick={() => toggleSearchDrawer()}
                    {...buttonProps}
                >
                    Search
                </Button>
            </div>
            <AnimatePresence>
                {searchDrawerOpen && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: pxToRem(57) }}
                        exit={{ height: 0 }}
                        className={css(S.MobileSearchDrawer)}
                    >
                        <InPageSearchBar
                            {...tabSearch}
                            toggleSearchDrawer={() => toggleSearchDrawer()}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <FilterDrawer
                open={filterDrawerOpen}
                onOpenChange={(_, data) => setFilterDrawerState(!!data)}
                filterSections={filters ?? []}
                filterStates={filterStates}
                category={category}
                onApplyFilters={(_, data) => {
                    setFilterDrawerState(false);
                    resetFilters(data);
                }}
                ignoreFilterKeys={ignoreFilterKeys}
            />
        </>
    );
};
